import { formatParisDateTime, type ExportFileListItem, type OrderCsvPreview, type OrderDetail } from '@flux/shared';
import { Download, FileCode } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { ApiError, downloadFromApi } from '@/lib/api/client.js';
import { useOrderCsvPreview, useOrderFiles } from '../use-orders-queries.js';

type CsvPreviewCell = OrderCsvPreview['header'][number];

/** Valeur du sélecteur (et de `?fileId=`) désignant l'aperçu recalculé, non envoyé. */
const PREVIEW_VALUE = 'preview';

/**
 * Un bloc du fichier (IC1 ou une ligne IC2) : sous-titre puis tableau collé
 * aux bords, séparé du bloc précédent par un filet — tous les blocs partagent
 * le même panneau.
 */
function CsvCellsTable({
  cells,
  title,
}: {
  cells: readonly CsvPreviewCell[];
  title: string;
}): JSX.Element {
  return (
    <section className="border-t border-border first:border-t-0">
      <h3 className="border-b border-border bg-muted/40 px-4 py-2.5 text-sm font-semibold">
        {title}
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-right">#</TableHead>
            <TableHead>Champ Martelet</TableHead>
            <TableHead>Valeur</TableHead>
            <TableHead className="text-right">Taille max</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cells.map((cell) => (
            <TableRow
              key={cell.position}
              className={cell.truncated ? 'bg-status-orange-bg/60' : undefined}
            >
              <TableCell className="w-12 text-right tabular-nums text-muted-foreground">
                {cell.position}
              </TableCell>
              <TableCell className="whitespace-nowrap">{cell.name}</TableCell>
              <TableCell className="break-all font-mono text-xs">
                {cell.value === '' ? (
                  <span className="text-muted-foreground">(vide)</span>
                ) : (
                  cell.value
                )}
                {cell.truncated ? (
                  <Badge tone="orange" className="ml-2">
                    Tronqué
                  </Badge>
                ) : null}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {cell.maxLength ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

export interface CsvTabProps {
  orderId: string;
  order: OrderDetail;
}

/**
 * L'aperçu recalculé n'a d'intérêt que s'il est plus récent que le fichier
 * courant : à la préparation, il est calculé en même temps que le fichier.
 * Il devient plus récent après « Mettre à jour depuis Shopify ».
 */
function isPreviewNewer(order: OrderDetail, files: readonly ExportFileListItem[]): boolean {
  if (!order.preview?.csvContent) return false;
  if (order.currentFileId === null) return true;
  const currentFile = files.find((file) => file.id === order.currentFileId);
  return currentFile !== undefined && new Date(order.preview.calculatedAt) > new Date(currentFile.createdAt);
}

/**
 * Onglet « Aperçu CSV » (`02` §4) : colonne par colonne, nom Martelet + taille
 * max, champ tronqué surligné. Le sélecteur n'apparaît que s'il y a le choix :
 * plusieurs versions du fichier, ou un aperçu plus récent que le fichier.
 */
export function CsvTab({ orderId, order }: CsvTabProps): JSX.Element {
  const filesQuery = useOrderFiles(orderId);
  const files = filesQuery.data ?? [];
  const previewNewer = isPreviewNewer(order, files);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const selection = selected ?? (previewNewer ? PREVIEW_VALUE : (order.currentFileId ?? undefined));
  const csvQuery = useOrderCsvPreview(orderId, selection, selection !== undefined);
  const [showRaw, setShowRaw] = useState(false);

  if (selection === undefined) {
    return (
      <EmptyState
        bordered
        title="Aucun fichier CSV généré"
        description={
          order.status === 'blocked'
            ? 'Commande bloquée : aucun fichier n’a été généré.'
            : 'Cette commande n’a pas encore été préparée : aucun aperçu à afficher.'
        }
      />
    );
  }

  const optionCount = files.length + (previewNewer ? 1 : 0);
  const showingPreview = selection === PREVIEW_VALUE;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 sm:w-auto">
          {optionCount > 1 ? (
            <Select value={selection} onValueChange={setSelected}>
              <SelectTrigger className="w-full sm:w-80" aria-label="Version du CSV">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {previewNewer ? (
                  <SelectItem value={PREVIEW_VALUE}>Aperçu à jour (non envoyé)</SelectItem>
                ) : null}
                {files.map((file) => (
                  <SelectItem key={file.id} value={file.id}>
                    {file.id === order.currentFileId ? `${file.name} (courant)` : file.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {csvQuery.data && optionCount <= 1 ? (
            <p className="min-w-0 break-all text-xs text-muted-foreground">
              Fichier : {csvQuery.data.fileName}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!csvQuery.data?.fileId}
            onClick={() => {
              if (csvQuery.data?.fileId) {
                downloadFromApi(`/api/files/${csvQuery.data.fileId}/download`);
              }
            }}
          >
            <Download aria-hidden="true" />
            Télécharger
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!csvQuery.data}
            onClick={() => {
              setShowRaw(true);
            }}
          >
            <FileCode aria-hidden="true" />
            Voir le brut
          </Button>
        </div>
      </div>

      {showingPreview && order.preview ? (
        <p className="text-xs text-muted-foreground">
          Calculé le {formatParisDateTime(new Date(order.preview.calculatedAt))} avec les données Shopify les plus
          récentes, non envoyé.{' '}
          {order.fileIds.length > 0
            ? 'Pour l’envoyer : « Renvoyer la commande à Martelet ».'
            : 'Recalculé avant l’envoi réel.'}
        </p>
      ) : null}

      {csvQuery.isPending ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          <Skeleton className="h-4 w-40" />
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={`csv-skeleton-${String(index)}`} className="h-3.5 w-full" />
          ))}
        </div>
      ) : null}
      {csvQuery.isError ? (
        <ErrorState
          message={
            csvQuery.error instanceof ApiError
              ? csvQuery.error.message
              : 'Impossible de charger cet aperçu.'
          }
          detail={csvQuery.error instanceof ApiError ? csvQuery.error.detail : undefined}
          onRetry={() => void csvQuery.refetch()}
        />
      ) : null}

      {csvQuery.data ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <CsvCellsTable cells={csvQuery.data.header} title="Ligne commande (IC1)" />
          {csvQuery.data.lines.map((line, index) => (
            <CsvCellsTable
              key={`line-${String(index)}`}
              cells={line}
              title={`Ligne produit ${String(index + 1)} (IC2)`}
            />
          ))}
        </div>
      ) : null}

      <Dialog open={showRaw} onOpenChange={setShowRaw}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="break-all">
              Contenu brut — {csvQuery.data?.fileName}
            </DialogTitle>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs">
            {csvQuery.data?.raw}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
